/**
 * DTOs du domaine payroll (gestion de paie) — Phase 9.
 *
 * Pas de service dédié (plan Phase 9, guide §6.3) : la paie est un TYPE de
 * transaction du wallet-service (`PAYROLL_CREDIT`, annexe A) et le devis de
 * frais celui du fee-engine (`POST /fee-quotes`, §6.5). Les interfaces
 * ci-dessous répliquent le sous-ensemble utile des DTOs canoniques
 * (`@/features/wallet/api/dto`, `@/features/fee-engine/api/dto`) — règle
 * d'architecture : jamais d'import croisé entre features, seuls les enums
 * de `@/features/shared` et les endpoints ré-exportés par
 * `@/core/api/endpoints` sont partagés.
 */
import type {
  CustomerSegment,
  DestinationExternalType,
  FeePayerFeeEngine,
  TransactionStatus,
} from "@/features/shared/api/enums";

/** Type de transaction wallet-service portant un versement de paie (annexe A). */
export const PAYROLL_TRANSACTION_TYPE = "PAYROLL_CREDIT";

/** Corps d'un versement de paie — `POST /Transactions/initiate` (§5.3). */
export interface PayrollInitiateRequestDto {
  type: typeof PAYROLL_TRANSACTION_TYPE;
  productCode: string;
  sourceWalletId: string;
  destinationWalletId: string;
  destinationExternalType: DestinationExternalType;
  /** Montant du versement, entier en unité mineure (§4.3). */
  amountMinor: number;
  currency: string;
  customerSegment: CustomerSegment;
  /** Party payeur — l'entreprise dont le back-office opère la paie (§6.3). */
  partyId?: string | null;
  /** Référence métier du lot de paie (ex. « Paie 2026-09 »). */
  customerReference?: string | null;
}

/** Transaction créée — sous-ensemble du TransactionDto du guide §6.3. */
export interface PayrollTransactionDto {
  id: string;
  reference: string;
  destinationWalletId?: string | null;
  amountMinor: number;
  feeAmountMinor: number;
  currency: string;
  status: TransactionStatus;
}

/** Compte rendu d'un lot de paie : une initiation par bénéficiaire. */
export interface PayrollRunReport {
  /** Versements initiés avec succès (dans l'ordre du lot). */
  succeeded: PayrollTransactionDto[];
  /** Versements en échec : indice du bénéficiaire dans le lot + message. */
  failed: { index: number; message: string }[];
}

/* --------------------------------- Devis de frais -------------------------------- */

/**
 * Corps d'un devis — `POST /fee-quotes` (§6.5). Aligné sur le body Postman
 * `FeeQuoteRequestDto` : `transactionReference` est **requis** par le
 * fee-engine (désérialisation JSON). Les identifiants optionnels partent
 * en `null` explicite (`providerId`, `beneficiaryPartyId`, `promoCode`,
 * `partyId`).
 */
export interface PayrollFeeQuoteRequestDto {
  productCode: string;
  providerId?: string | null;
  customerSegment: CustomerSegment;
  feePurpose: "CUSTOMER_FEE";
  /** Montant du versement, entier en unité mineure (§4.3). */
  amountMinor: number;
  currency: string;
  /**
   * Référence de corrélation du devis (journalisation fee-engine).
   * Le lot n'existe pas encore à la preview : UUID client, unique par
   * bénéficiaire. Distinct de `customerReference` (initiate wallet).
   */
  transactionReference: string;
  beneficiaryPartyId?: string | null;
  promoCode?: string | null;
  partyId?: string | null;
}

/** Réponse d'un devis unitaire — sous-ensemble du DTO canonique (§6.5). */
export interface PayrollFeeQuoteResponseDto {
  grossFeeAmountMinor: number;
  discountAmountMinor: number;
  netFeeAmountMinor: number;
  payer: FeePayerFeeEngine;
  currency: string;
  /** Aucun barème actif pour le triplet demandé — facturation à 0. */
  noActiveRule: boolean;
}

/**
 * Devis AGRÉGÉ d'un lot de paie : la somme des devis unitaires (un appel
 * `POST /fee-quotes` par bénéficiaire — le fee-engine n'a pas d'endpoint
 * de lot). Montants en unité mineure.
 */
export interface PayrollAggregateFeeQuoteDto {
  grossFeeAmountMinor: number;
  discountAmountMinor: number;
  netFeeAmountMinor: number;
  currency: string;
  /** Vrai si AU MOINS un versement n'a pas de règle de frais active. */
  noActiveRule: boolean;
}

/* ============================================================================
 * Phase 22 — Paie de masse (payroll-service v4.1, §6.11).
 *
 * Nouvelle ressource `PayrollBatches` exposée à `flashpay-merchant` (titulaire)
 * et au back-office. Flux : add / import → simulate → launch (idempotent) →
 * refresh → relevés PDF / ZIP. Les Phase 9 DTOs ci-dessus restent valides
 * (type de transaction `PAYROLL_CREDIT` du wallet-service, utilisé par
 * payroll-service en interne) et ne doivent pas être supprimés.
 * ========================================================================== */

import type {
  PayrollBatchStatus,
  PayrollLineStatus,
  PayrollFeePayer,
  PayrollBeneficiaryType,
} from "./enums";

/** Une ligne de paie (un salarié) telle que lue dans les réponses v4.1. */
export interface PayrollBatchLineDto {
  id: string;
  employeeName: string;
  employeeReference?: string | null;
  /** Numéro E.164 (ou saisi national, normalisé par le serveur). */
  phone: string;
  amountMinor: number;
  /** Statut au fil du cycle de vie (PENDING → VALID → PROCESSING → PAID…). */
  status: PayrollLineStatus;
  /** Motif d'invalidité renseigné par le serveur (numéro mal formé, doublon…). */
  invalidReason?: string | null;
  /** Membre (portefeuille actif) ou non-membre (code de retrait émis). */
  beneficiaryType?: PayrollBeneficiaryType | null;
  /** Frais calculés à la simulation (produit `PAYROLL_SALARY_PAYMENT`). */
  feeAmountMinor?: number | null;
  /** Montant net reçu par le salarié (= grossMinor - feeMinor si RECEIVER). */
  netAmountMinor?: number | null;
  /** Référence de la transaction wallet (FPT-…) une fois la ligne payée. */
  transactionReference?: string | null;
  /** Motif d'échec de versement (hors invalidité métier). */
  failureReason?: string | null;
}

/** Détail d'une paie de masse (GET /PayrollBatches/{id}). */
export interface PayrollBatchDto {
  id: string;
  label: string;
  feePayer: PayrollFeePayer;
  status: PayrollBatchStatus;
  currency: string;
  lineCount: number;
  /** Nombre de lignes `INVALID` à corriger (DRAFT / SIMULATED). */
  invalidCount: number;
  /** Masse salariale brute (somme des `amountMinor` des lignes). */
  grossAmountMinor: number;
  /** Total des frais calculés à la simulation. */
  feeAmountMinor: number;
  /** Montant à débiter sur le portefeuille de règlement. */
  debitAmountMinor: number;
  /** Solde disponible constaté à la simulation. */
  availableBalanceMinor: number;
  /** Vrai si le solde couvre la paie entière au moment de la simulation. */
  sufficientBalance: boolean;
  /** Peut-on lancer maintenant ? (simulation récente + solde suffisant). */
  canLaunch: boolean;
  /** Validité de la simulation (ISO UTC, par défaut 30 min). Null hors SIMULATED. */
  simulationExpiresAt?: string | null;
  /** Référence `FPY-…` de la paie côté wallet-service (une fois lancée). */
  disbursementReference?: string | null;
  /** Nombre de salariés effectivement payés. */
  paidCount: number;
  createdAt: string;
  updatedAt?: string | null;
  /** Horodatage de lancement (si lancée). */
  launchedAt?: string | null;
  /** Horodatage d'annulation (si CANCELLED). */
  cancelledAt?: string | null;
  lines: PayrollBatchLineDto[];
}

/** Corps d'une ligne à créer / éditer (sans l'identifiant). */
export interface PayrollBatchLineInputDto {
  employeeName: string;
  employeeReference?: string | null;
  phone: string;
  amountMinor: number;
}

/** Corps de `POST /PayrollBatches/add`. */
export interface PayrollBatchCreateRequestDto {
  label: string;
  feePayer: PayrollFeePayer;
  lines: PayrollBatchLineInputDto[];
}

/** Corps de `PUT /PayrollBatches/{id}` (label et/ou feePayer). */
export interface PayrollBatchUpdateRequestDto {
  label?: string;
  feePayer?: PayrollFeePayer;
}

/** Filtre de `POST /PayrollBatches/all`. */
export interface PayrollBatchFilter {
  status?: PayrollBatchStatus;
  /** Borne basse de création (ISO date-time). */
  from?: string;
  /** Borne haute de création (ISO date-time). */
  to?: string;
}

/** Corps de `POST /PayrollBatches/all` (pagination §4.2). */
export interface PayrollBatchListRequestDto {
  page: number;
  pageSize: number;
  filter?: PayrollBatchFilter;
}

/** Entrée abrégée d'une paie dans la liste paginée (vue tableau). */
export interface PayrollBatchListItemDto {
  id: string;
  label: string;
  feePayer: PayrollFeePayer;
  status: PayrollBatchStatus;
  currency: string;
  lineCount: number;
  paidCount: number;
  grossAmountMinor: number;
  debitAmountMinor: number;
  createdAt: string;
  launchedAt?: string | null;
}

/** Réponse de `GET /PayrollBatches/stats`. */
export interface PayrollBatchStatsResponseDto {
  /** Nombre total de paies créées sur la période. */
  batchCount: number;
  /** Paies effectivement payées (status COMPLETED / PARTIALLY_COMPLETED). */
  paidBatchCount: number;
  /** Salariés payés (membres + non-membres). */
  employeesPaid: number;
  /** Dont salariés membres (FlashPay). */
  memberEmployeesPaid?: number;
  /** Dont salariés non-membres (code de retrait). */
  nonMemberEmployeesPaid?: number;
  /** Masse salariale brute cumulée (somme des `grossAmountMinor`). */
  grossAmountMinor: number;
  /** Frais cumulés. */
  feeAmountMinor: number;
  /** Total débité (= grossAmountMinor si feePayer=RECEIVER, +fee si SENDER). */
  debitAmountMinor: number;
  currency: string;
}

/** Réponse de `POST /PayrollBatches/{id}/simulate` — paie entière SIMULATED. */
export type PayrollBatchSimulateResponseDto = PayrollBatchDto;

/**
 * Réponse de `POST /PayrollBatches/{id}/launch` — paie entière passée à
 * `PROCESSING` (ou `COMPLETED` / `PARTIALLY_COMPLETED` si synchrone).
 */
export type PayrollBatchLaunchResponseDto = PayrollBatchDto;

/** Réponse de `POST /PayrollBatches/{id}/refresh` — paie entière rafraîchie. */
export type PayrollBatchRefreshResponseDto = PayrollBatchDto;

/** Réponse de `POST /PayrollBatches/{id}/cancel` — paie `CANCELLED`. */
export type PayrollBatchCancelResponseDto = PayrollBatchDto;

/** Corps multipart de `POST /PayrollBatches/import`. */
export interface PayrollBatchImportFormData {
  file: File;
  label: string;
  feePayer: PayrollFeePayer;
}
